import type { ClientLogInput, ClientLogLevel } from '@cult/schemas';
import { ApiError, apiFetch } from '../../lib/api-client';

const truncate = (value: string, max: number): string => (value.length > max ? value.slice(0, max) : value);

export const toClientLog = (source: string, error: unknown, level: ClientLogLevel): ClientLogInput => {
  const base = { level, source, path: truncate(window.location.pathname, 500) };
  if (error instanceof ApiError) {
    return {
      ...base,
      errorName: error.name,
      message: truncate(error.message, 2_000),
      status: error.status,
      code: error.code,
    };
  }
  if (error instanceof Error) {
    return {
      ...base,
      errorName: truncate(error.name, 100),
      message: truncate(error.message, 2_000),
      stack: error.stack ? truncate(error.stack, 4_000) : undefined,
    };
  }
  return { ...base, message: truncate(String(error), 2_000) };
};

/**
 * Sends a browser-side error to `POST /client-logs`, which writes it straight into the API logs.
 * Fire-and-forget: never throws, never retries — reporting must not break the page it reports on.
 */
export const reportClientError = (source: string, error: unknown, level: ClientLogLevel = 'error'): void => {
  // 401 means the session is gone: the query client already redirects to /login,
  // and the report would be rejected by the same session guard anyway.
  if (error instanceof ApiError && error.status === 401) return;

  apiFetch('/client-logs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toClientLog(source, error, level)),
    // Lets the report finish even if the user navigates away right after the error.
    keepalive: true,
  }).catch(() => undefined);
};
